/* eslint-disable no-script-url */
/* eslint-disable consistent-return */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  useDataLayerAndAnalyticsLinks,
  useSubmitSavedApplication,
  useUpdatePOIType,
  useErrorTrackingAndUpdateStore,
  useErrorTracking,
} from 'hooks';
import { Grid, Button, Stack } from 'basis';
import { useStoreValue } from 'store';
import { LayoutPage, Errors, Form, SaveAndCompleteLaterButton, LayoutContent } from 'components';
import { CHANNEL_INSTORE } from '_config';
import { submitHandler, isAU, isNZ } from 'utils';
import { isFeatureOn } from 'featureToggles';
import { UploadPayslips } from './UploadPayslips';
import { UploadBankStatements } from './UploadBankStatements';
import { UploadOtherDocuments } from './UploadOtherDocuments';

export function VerifyDocumentUpload() {
  const [storeState, updateStore] = useStoreValue();
  const [uploading, setUploading] = useState(false);
  const history = useHistory();

  const { trackEventSender } = useDataLayerAndAnalyticsLinks(storeState);
  const { handleErrorCallback } = useErrorTrackingAndUpdateStore();
  const { handleErrorTracking } = useErrorTracking();
  const {
    applicationRef,
    acquisition,
    incomeVerificationType,
    applyAndBuy: { channel },
  } = storeState;

  const { employmentStatus } = acquisition.employmentDetails;
  const auDmsUpload = isFeatureOn('auDmsUpload');
  const showUploadOtherDocuments = incomeVerificationType === 'POITYPE_DOC_UPLOAD';
  const showUploadPayslips = incomeVerificationType === 'POITYPE_PAYSLIP_UPLOAD';
  const showUploadBankStatements = incomeVerificationType === 'POITYPE_BANK_STATEMENT_UPLOAD';
  const showDMSView =
    (isAU() && auDmsUpload && (showUploadPayslips || showUploadOtherDocuments || showUploadBankStatements)) || isNZ();
  const dmsChannel = channel === CHANNEL_INSTORE ? 'INSTORE' : 'ONLINE';

  const initialValues = useMemo(
    () => ({
      incomeEmploymentType: '',
    }),
    [],
  );
  const methodsRef = useRef(null);

  const handleStartUpload = async () => {
    setUploading(true);
    await methodsRef.current?.startUpload?.();
    setUploading(false);
  };

  const goToProcessingPage = () => {
    const redirectUrl = '/processing-pending-poi';
    updateStore({
      activeStep: redirectUrl,
    });
    history.push(redirectUrl);
  };

  const goToProcessingPendingPoiPage = () => {
    updateStore({
      activeStep: '/processing-pending-poi',
    });
    history.push('/processing-pending-poi');
  };

  const goToReplayIncomePage = useCallback(() => {
    updateStore({
      activeStep: '/confirm-income',
    });
    history.push('/confirm-income');
  }, [history, updateStore]);

  const { loading } = useSubmitSavedApplication({
    storeState,
    onSuccess: showDMSView ? goToProcessingPage : goToProcessingPendingPoiPage,
    onError: handleErrorCallback,
  });
  const { updatePOI } = useUpdatePOIType({
    storeState,
    onSuccess: showDMSView ? goToProcessingPage : goToProcessingPendingPoiPage,
    onError: handleErrorCallback,
  });

  const updatePoiVerificationType = useCallback(
    poiMethod => {
      return updatePOI(poiMethod);
    },
    [updatePOI],
  );

  const onSubmit = ({ errors }) => {
    const updateStoreAndRedirectToProcessing = () => {
      trackEventSender({
        category: 'application',
        action: 'application-navigation',
        location: 'upload-documents',
        label: 'Upload documents now',
      });
      updatePoiVerificationType(storeState.incomeVerificationType);
    };
    submitHandler({ submit: updateStoreAndRedirectToProcessing, errors });
  };

  if (storeState.applicationErrors) {
    const retry = () => {
      updateStore({
        applicationErrors: null,
      });

      if (isFeatureOn('incomeReplay')) {
        goToReplayIncomePage();
      } else {
        updatePoiVerificationType(storeState.incomeVerificationType);
      }
    };

    return (
      <LayoutPage>
        <Errors retry={retry} retrying={loading} />
      </LayoutPage>
    );
  }

  const UPLOAD_TITLES = {
    PAYSLIPS: 'Upload your payslips',
    BANK_STATEMENTS: 'Upload your bank statements',
    DOCUMENTS: 'Upload your documents',
  };

  const handleDocUploadEvent = evt => {
    switch (evt.type) {
      case 'ERROR':
        handleErrorTracking(evt.error?.message ?? evt.error);
        break;
      case 'COMPLETE':
        updateStore({ incomeDocumentsUploaded: true });
        updatePoiVerificationType(storeState.incomeVerificationType);
        break;
      case 'INIT':
        methodsRef.current = {
          startUpload: evt.data.startUpload,
        };
        break;
      default:
        break;
    }
  };

  const getHeaderOverride = () => {
    const isEnabled = (isAU() && auDmsUpload) || isNZ();
    if (!isEnabled) return undefined;
    return showUploadPayslips
      ? UPLOAD_TITLES.PAYSLIPS
      : showUploadBankStatements
      ? UPLOAD_TITLES.BANK_STATEMENTS
      : showUploadOtherDocuments
      ? UPLOAD_TITLES.DOCUMENTS
      : undefined;
  };

  return (
    <LayoutPage headerOverride={getHeaderOverride()}>
      <LayoutContent>
        <Form
          initialValues={initialValues}
          id="incomeDocumentUpload"
          onSubmit={onSubmit}
          disableFormCache
          showBackButton
          hideFormButtons
          submitButtonLabel="Upload documents now"
        >
          {formData => {
            return (
              <Stack gap="10">
                {showUploadPayslips && (
                  <UploadPayslips
                    applicationRef={applicationRef}
                    handleDocUploadEvent={handleDocUploadEvent}
                    channel={dmsChannel}
                  />
                )}
                {showUploadBankStatements && (
                  <UploadBankStatements
                    applicationRef={applicationRef}
                    handleDocUploadEvent={handleDocUploadEvent}
                    channel={dmsChannel}
                  />
                )}
                {showUploadOtherDocuments && (
                  <UploadOtherDocuments
                    applicationRef={applicationRef}
                    employmentStatus={employmentStatus}
                    channel={dmsChannel}
                    handleDocUploadEvent={handleDocUploadEvent}
                  />
                )}

                <Grid>
                  <Grid.Item alignItems="center" colSpan="all">
                    <Grid rowsGap="4" colsGap="2">
                      <Grid.Item colSpan="all" colSpan-sm="0-4" rowSpan-sm="0">
                        <Grid>
                          <SubmitButton onClick={handleStartUpload} loading={uploading} />
                        </Grid>
                      </Grid.Item>
                      <Grid.Item colSpan="all" colSpan-sm="5-7" alignItems="center">
                        <Grid>
                          <SaveAndCompleteLaterButton />
                        </Grid>
                      </Grid.Item>
                    </Grid>
                  </Grid.Item>
                </Grid>
              </Stack>
            );
          }}
        </Form>
      </LayoutContent>
    </LayoutPage>
  );
}

const SubmitButton = ({ onClick, loading }) => {
  return (
    <Button width="100%" type="button" disabled={loading} onClick={onClick}>
      Submit
    </Button>
  );
};
